import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { categoriesAPI, productsAPI, ordersAPI, shopsAPI, usersAPI } from '../services/api';
import { Order, Shop, Product } from '../types';
import { useAuth } from '../contexts/AuthContext';
import BusinessReport from '../components/BusinessReport';
import './Dashboard.css';

const fmt = (n: number) =>
  Number(n).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDateTime = (dateStr: string) => {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Ожидает',
  CONFIRMED: 'Подтверждён',
  SHIPPED: 'Отправлен',
  DELIVERED: 'Доставлен',
  CANCELLED: 'Отменён',
  REJECTED: 'Отклонён',
};

const STATUS_COLORS: Record<string, string> = {
  PENDING: '#f59e0b',
  CONFIRMED: '#3b82f6',
  SHIPPED: '#8b5cf6',
  DELIVERED: '#10b981',
  CANCELLED: '#ef4444',
  REJECTED: '#ef4444',
};

const STATUS_CONFIG: Record<string, { label: string; bg: string; color: string; dot: string }> = {
  PENDING: { label: 'Ожидает', bg: '#fef3c7', color: '#92400e', dot: '#f59e0b' },
  CONFIRMED: { label: 'Подтверждён', bg: '#eff6ff', color: '#1e40af', dot: '#3b82f6' },
  SHIPPED: { label: 'Отправлен', bg: '#f5f3ff', color: '#5b21b6', dot: '#8b5cf6' },
  DELIVERED: { label: 'Доставлен', bg: '#ecfdf5', color: '#065f46', dot: '#10b981' },
  CANCELLED: { label: 'Отменён', bg: '#fef2f2', color: '#991b1b', dot: '#ef4444' },
  REJECTED: { label: 'Отклонён', bg: '#fef2f2', color: '#991b1b', dot: '#ef4444' },
};

const PAYMENT_CONFIG: Record<string, { label: string; icon: string; bg: string; color: string }> = {
  CASH: { label: 'Наличные', icon: '💵', bg: '#f0fdf4', color: '#166534' },
  CREDIT: { label: 'В долг', icon: '💳', bg: '#fff7ed', color: '#9a3412' },
  ADVANCE: { label: 'Аванс', icon: '💰', bg: '#eff6ff', color: '#1e40af' },
};

// ─── Reusable Recent Orders Widget ──────────────────────────────────────────
interface RecentOrdersWidgetProps {
  orders: Order[];
  showAgent?: boolean;
  viewAllLink?: string;
}

const RecentOrdersWidget: React.FC<RecentOrdersWidgetProps> = ({
  orders,
  showAgent = true,
  viewAllLink = '/orders',
}) => {
  return (
    <div className="recent-orders-widget">
      <div className="recent-orders-header">
        <div className="recent-orders-title-wrap">
          <span className="recent-orders-icon">📋</span>
          <h2 className="recent-orders-title">Последние заказы</h2>
          {orders.length > 0 && (
            <span className="recent-orders-count">{orders.length}</span>
          )}
        </div>
        <Link to={viewAllLink} className="recent-orders-link">
          Все заказы <span className="arrow">→</span>
        </Link>
      </div>

      {orders.length === 0 ? (
        <div className="recent-orders-empty">
          <div className="empty-icon">📦</div>
          <p className="empty-title">Заказов пока нет</p>
          <p className="empty-sub">Новые оформленные заказы отобразятся в этом списке</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="recent-orders-table-wrap">
            <table className="recent-orders-table">
              <thead>
                <tr>
                  <th style={{ width: '90px' }}>№ Заказа</th>
                  <th>Магазин</th>
                  {showAgent && <th>Агент</th>}
                  <th style={{ width: '130px' }}>Оплата</th>
                  <th style={{ width: '130px' }}>Сумма</th>
                  <th style={{ width: '140px' }}>Статус</th>
                  <th style={{ width: '150px' }}>Дата</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const status = STATUS_CONFIG[o.status] || {
                    label: o.status,
                    bg: '#f1f5f9',
                    color: '#475569',
                    dot: '#94a3b8',
                  };
                  const payment = PAYMENT_CONFIG[o.paymentType] || {
                    label: o.paymentType,
                    icon: '💳',
                    bg: '#f8fafc',
                    color: '#475569',
                  };

                  return (
                    <tr key={o.id} className="recent-order-row">
                      <td className="cell-id">
                        <span className="order-id-chip">#{o.id}</span>
                      </td>
                      <td className="cell-shop">
                        <div className="order-shop-cell">
                          <span className="shop-name">{o.shop?.name || '—'}</span>
                          {o.shop?.address && (
                            <span className="shop-sub">{o.shop.address}</span>
                          )}
                        </div>
                      </td>
                      {showAgent && (
                        <td className="cell-agent">
                          <div className="order-agent-cell">
                            <span className="agent-avatar">
                              {o.agent?.firstName ? o.agent.firstName.charAt(0).toUpperCase() : '👤'}
                            </span>
                            <span className="agent-name">
                              {o.agent ? `${o.agent.firstName} ${o.agent.lastName || ''}`.trim() : '—'}
                            </span>
                          </div>
                        </td>
                      )}
                      <td className="cell-payment">
                        <span
                          className="payment-type-chip"
                          style={{ backgroundColor: payment.bg, color: payment.color }}
                        >
                          <span className="payment-icon">{payment.icon}</span>
                          <span>{payment.label}</span>
                        </span>
                      </td>
                      <td className="cell-amount">
                        <span className="order-amount">{fmt(o.totalAmount)} с</span>
                      </td>
                      <td className="cell-status">
                        <span
                          className="order-status-pill"
                          style={{
                            backgroundColor: status.bg,
                            color: status.color,
                          }}
                        >
                          <span className="status-dot-indicator" style={{ backgroundColor: status.dot }} />
                          {status.label}
                        </span>
                      </td>
                      <td className="cell-date">
                        <span className="order-date">{formatDateTime(o.createdAt)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards View */}
          <div className="recent-orders-mobile-list">
            {orders.map((o) => {
              const status = STATUS_CONFIG[o.status] || {
                label: o.status,
                bg: '#f1f5f9',
                color: '#475569',
                dot: '#94a3b8',
              };
              const payment = PAYMENT_CONFIG[o.paymentType] || {
                label: o.paymentType,
                icon: '💳',
                bg: '#f8fafc',
                color: '#475569',
              };

              return (
                <div key={o.id} className="recent-order-card">
                  <div className="order-card-header">
                    <div className="order-card-header-left">
                      <span className="order-id-chip">#{o.id}</span>
                      <span className="card-shop-name">{o.shop?.name || '—'}</span>
                    </div>
                    <span
                      className="order-status-pill"
                      style={{
                        backgroundColor: status.bg,
                        color: status.color,
                      }}
                    >
                      <span className="status-dot-indicator" style={{ backgroundColor: status.dot }} />
                      {status.label}
                    </span>
                  </div>

                  <div className="order-card-meta">
                    {showAgent && o.agent && (
                      <div className="meta-item">
                        <span className="meta-label">Агент:</span>
                        <span className="meta-value">{o.agent.firstName} {o.agent.lastName || ''}</span>
                      </div>
                    )}
                    <div className="meta-item-row">
                      <span
                        className="payment-type-chip"
                        style={{ backgroundColor: payment.bg, color: payment.color }}
                      >
                        <span className="payment-icon">{payment.icon}</span>
                        <span>{payment.label}</span>
                      </span>
                      <span className="order-date-small">{formatDateTime(o.createdAt)}</span>
                    </div>
                  </div>

                  <div className="order-card-footer">
                    <span className="amount-label">Сумма:</span>
                    <span className="card-order-amount">{fmt(o.totalAmount)} с</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Admin Dashboard ──────────────────────────────────────────────────────────
const AdminDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalCategories: 0,
    totalUsers: 0,
    totalShops: 0,
    totalDebt: 0,
    shopsOverLimit: 0,
    lowStockCount: 0,
  });
  const [orderStats, setOrderStats] = useState<Record<string, number>>({});
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [overdueOrders, setOverdueOrders] = useState<Order[]>([]);
  const [topDebtShops, setTopDebtShops] = useState<Shop[]>([]);
  const [lowStockProducts, setLowStockProducts] = useState<Product[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [categories, products, users, shops, orders, overdueRes] = await Promise.all([
          categoriesAPI.getAll(),
          productsAPI.getAll({ limit: 1000 }),
          usersAPI.getAll(),
          shopsAPI.getAll({ limit: 1000 }),
          ordersAPI.getAll({ limit: 5 }),
          ordersAPI.getAll({ overdue: true, limit: 50 }),
        ]);

        // Order status breakdown
        const allOrders = await ordersAPI.getAll({ limit: 1000 });
        const statusMap: Record<string, number> = {};
        allOrders.data.forEach((o) => {
          statusMap[o.status] = (statusMap[o.status] || 0) + 1;
        });

        // Low stock (< 10)
        const lowStock = products.data.filter((p) => !p.stock || Number(p.stock.quantity) < 10);

        // Debt stats
        const totalDebt = shops.data.reduce((s, sh) => s + Number(sh.debt), 0);
        const overLimit = shops.data.filter(
          (sh) => Number(sh.creditLimit) > 0 && Number(sh.debt) >= Number(sh.creditLimit),
        );
        const sortedByDebt = [...shops.data]
          .filter((sh) => Number(sh.debt) > 0)
          .sort((a, b) => Number(b.debt) - Number(a.debt))
          .slice(0, 5);

        setStats({
          totalProducts: products.meta.total,
          totalCategories: categories.length,
          totalUsers: users.length,
          totalShops: shops.meta.total,
          totalDebt,
          shopsOverLimit: overLimit.length,
          lowStockCount: lowStock.length,
        });
        setOrderStats(statusMap);
        setRecentOrders(orders.data);
        setOverdueOrders(overdueRes.data);
        setTopDebtShops(sortedByDebt);
        setLowStockProducts(lowStock.slice(0, 6));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <div className="dash-loading">Загрузка...</div>;

  const totalOrders = Object.values(orderStats).reduce((a, b) => a + b, 0);

  return (
    <div className="dashboard">
      <div className="dash-header">
        <h1 className="dash-title">Обзор системы</h1>
        <BusinessReport />
      </div>

      {/* KPI row */}
      <div className="kpi-grid">
        <div className="kpi-card kpi-blue">
          <div className="kpi-icon">🛍️</div>
          <div className="kpi-body">
            <span className="kpi-label">Товаров</span>
            <span className="kpi-value">{stats.totalProducts}</span>
          </div>
        </div>
        <div className="kpi-card kpi-purple">
          <div className="kpi-icon">🏪</div>
          <div className="kpi-body">
            <span className="kpi-label">Магазинов</span>
            <span className="kpi-value">{stats.totalShops}</span>
          </div>
        </div>
        <div className="kpi-card kpi-amber">
          <div className="kpi-icon">📋</div>
          <div className="kpi-body">
            <span className="kpi-label">Всего заказов</span>
            <span className="kpi-value">{totalOrders}</span>
          </div>
        </div>
        <div className={`kpi-card ${stats.totalDebt > 0 ? 'kpi-red' : 'kpi-green'}`}>
          <div className="kpi-icon">💰</div>
          <div className="kpi-body">
            <span className="kpi-label">Общий долг</span>
            <span className="kpi-value">{fmt(stats.totalDebt)} с</span>
          </div>
        </div>
        <div className={`kpi-card ${stats.shopsOverLimit > 0 ? 'kpi-red' : 'kpi-green'}`}>
          <div className="kpi-icon">⚠️</div>
          <div className="kpi-body">
            <span className="kpi-label">Превысили лимит</span>
            <span className="kpi-value">{stats.shopsOverLimit}</span>
          </div>
        </div>
        <div className={`kpi-card ${stats.lowStockCount > 0 ? 'kpi-amber' : 'kpi-green'}`}>
          <div className="kpi-icon">📦</div>
          <div className="kpi-body">
            <span className="kpi-label">Мало на складе</span>
            <span className="kpi-value">{stats.lowStockCount}</span>
          </div>
        </div>
        <div className={`kpi-card ${overdueOrders.length > 0 ? 'kpi-red' : 'kpi-green'}`}>
          <div className="kpi-icon">🔴</div>
          <div className="kpi-body">
            <span className="kpi-label">Просрочено долгов</span>
            <span className="kpi-value">{overdueOrders.length}</span>
          </div>
        </div>
      </div>

      <div className="dash-row">
        {/* Order status breakdown */}
        <div className="dash-card">
          <h2 className="dash-card-title">Статусы заказов</h2>
          <div className="status-list">
            {Object.entries(STATUS_LABELS).map(([key, label]) => {
              const count = orderStats[key] || 0;
              const pct = totalOrders ? Math.round((count / totalOrders) * 100) : 0;
              return (
                <div key={key} className="status-row">
                  <span className="status-dot" style={{ background: STATUS_COLORS[key] }} />
                  <span className="status-name">{label}</span>
                  <div className="status-bar-wrap">
                    <div className="status-bar" style={{ width: `${pct}%`, background: STATUS_COLORS[key] }} />
                  </div>
                  <span className="status-count">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top debt shops */}
        <div className="dash-card">
          <h2 className="dash-card-title">Топ долги по магазинам</h2>
          {topDebtShops.length === 0 ? (
            <p className="dash-empty">Долгов нет 🎉</p>
          ) : (
            <div className="debt-list">
              {topDebtShops.map((shop) => {
                const pct = Number(shop.creditLimit) > 0
                  ? Math.min(100, (Number(shop.debt) / Number(shop.creditLimit)) * 100)
                  : 0;
                const over = Number(shop.creditLimit) > 0 && Number(shop.debt) >= Number(shop.creditLimit);
                return (
                  <div key={shop.id} className="debt-row">
                    <div className="debt-row-top">
                      <span className="debt-name">{shop.name}</span>
                      <span className="debt-amount" style={{ color: over ? '#ef4444' : '#1e40af' }}>
                        {fmt(shop.debt)} с
                      </span>
                    </div>
                    {Number(shop.creditLimit) > 0 && (
                      <div className="debt-bar-wrap">
                        <div
                          className="debt-bar"
                          style={{ width: `${pct}%`, background: over ? '#ef4444' : '#3b82f6' }}
                        />
                      </div>
                    )}
                    {Number(shop.creditLimit) > 0 && (
                      <span className="debt-limit">лимит: {fmt(shop.creditLimit)} с</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="dash-row dash-row-main">
        {/* Recent orders */}
        <div className="dash-card dash-card-recent-orders">
          <RecentOrdersWidget orders={recentOrders} showAgent={true} viewAllLink="/orders" />
        </div>

        {/* Low stock */}
        {lowStockProducts.length > 0 && (
          <div className="dash-card dash-card-low-stock">
            <div className="recent-orders-header">
              <div className="recent-orders-title-wrap">
                <span className="recent-orders-icon">📦</span>
                <h2 className="recent-orders-title">Мало на складе</h2>
                <span className="recent-orders-count count-amber">{lowStockProducts.length}</span>
              </div>
              <Link to="/stock" className="recent-orders-link">
                Склад <span className="arrow">→</span>
              </Link>
            </div>
            <div className="low-stock-list">
              {lowStockProducts.map((p) => (
                <div key={p.id} className="low-stock-row">
                  <span className="low-stock-name">{p.name}</span>
                  <span className={`low-stock-qty ${Number(p.stock?.quantity) === 0 ? 'qty-zero' : 'qty-low'}`}>
                    {p.stock?.quantity ?? 0} {p.unit === 'PIECE' ? 'шт.' : p.unit === 'METER' ? 'м.' : 'кг.'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Overdue debts */}
      {overdueOrders.length > 0 && (
        <div className="dash-row" style={{ gridTemplateColumns: '1fr' }}>
          <div className="dash-card overdue-card">
            <h2 className="dash-card-title">🔴 Просроченные долги ({overdueOrders.length})</h2>
            <table className="dash-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Магазин</th>
                  <th>Агент</th>
                  <th>Сумма</th>
                  <th>Срок был</th>
                  <th>Просрочено дней</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody>
                {overdueOrders.map((o) => {
                  const due = new Date(o.dueDate!);
                  const today = new Date();
                  const days = Math.floor((today.getTime() - due.getTime()) / 86400000);
                  return (
                    <tr key={o.id} className="overdue-row">
                      <td>#{o.id}</td>
                      <td style={{ fontWeight: 600 }}>{o.shop?.name || '—'}</td>
                      <td>{o.agent?.firstName} {o.agent?.lastName || ''}</td>
                      <td style={{ fontWeight: 700, color: '#dc2626' }}>{fmt(o.totalAmount)} с</td>
                      <td style={{ color: '#dc2626' }}>{new Date(o.dueDate!).toLocaleDateString('ru-RU')}</td>
                      <td>
                        <span className="overdue-days">+{days} дн.</span>
                      </td>
                      <td>
                        <span className="dash-badge" style={{ background: STATUS_COLORS[o.status] + '22', color: STATUS_COLORS[o.status] }}>
                          {STATUS_LABELS[o.status]}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
};

// ─── Agent Dashboard ──────────────────────────────────────────────────────────
const AgentDashboard: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [orderStats, setOrderStats] = useState<Record<string, number>>({});
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [totalRevenue, setTotalRevenue] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const all = await ordersAPI.getAll({ limit: 1000 });
        const statusMap: Record<string, number> = {};
        let revenue = 0;
        all.data.forEach((o) => {
          statusMap[o.status] = (statusMap[o.status] || 0) + 1;
          if (o.status === 'DELIVERED') revenue += Number(o.totalAmount);
        });
        setOrderStats(statusMap);
        setTotalRevenue(revenue);
        setRecentOrders(all.data.slice(0, 5));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <div className="dash-loading">Загрузка...</div>;

  const pending = orderStats['PENDING'] || 0;
  const delivered = orderStats['DELIVERED'] || 0;
  const total = Object.values(orderStats).reduce((a, b) => a + b, 0);

  return (
    <div className="dashboard">
      <h1 className="dash-title">Добро пожаловать, {user?.firstName}!</h1>

      <div className="kpi-grid">
        <div className="kpi-card kpi-blue">
          <div className="kpi-icon">📋</div>
          <div className="kpi-body">
            <span className="kpi-label">Всего заказов</span>
            <span className="kpi-value">{total}</span>
          </div>
        </div>
        <div className="kpi-card kpi-amber">
          <div className="kpi-icon">⏳</div>
          <div className="kpi-body">
            <span className="kpi-label">Ожидают</span>
            <span className="kpi-value">{pending}</span>
          </div>
        </div>
        <div className="kpi-card kpi-green">
          <div className="kpi-icon">✅</div>
          <div className="kpi-body">
            <span className="kpi-label">Доставлено</span>
            <span className="kpi-value">{delivered}</span>
          </div>
        </div>
        <div className="kpi-card kpi-purple">
          <div className="kpi-icon">💵</div>
          <div className="kpi-body">
            <span className="kpi-label">Сумма доставленных</span>
            <span className="kpi-value">{fmt(totalRevenue)} с</span>
          </div>
        </div>
      </div>

      <div className="dash-row">
        <div className="dash-card">
          <h2 className="dash-card-title">Статусы заказов</h2>
          <div className="status-list">
            {Object.entries(STATUS_LABELS).map(([key, label]) => {
              const count = orderStats[key] || 0;
              const pct = total ? Math.round((count / total) * 100) : 0;
              return (
                <div key={key} className="status-row">
                  <span className="status-dot" style={{ background: STATUS_COLORS[key] }} />
                  <span className="status-name">{label}</span>
                  <div className="status-bar-wrap">
                    <div className="status-bar" style={{ width: `${pct}%`, background: STATUS_COLORS[key] }} />
                  </div>
                  <span className="status-count">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="dash-card dash-card-wide">
          <RecentOrdersWidget orders={recentOrders} showAgent={false} viewAllLink="/orders" />
        </div>
      </div>
    </div>
  );
};

// ─── Shop Owner Dashboard ─────────────────────────────────────────────────────
const ShopOwnerDashboard: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [shops, setShops] = useState<Shop[]>([]);
  const [orderStats, setOrderStats] = useState<Record<string, number>>({});
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [shopsRes, ordersRes] = await Promise.all([
          shopsAPI.getAll({ limit: 100 }),
          ordersAPI.getAll({ limit: 1000 }),
        ]);
        setShops(shopsRes.data);
        const statusMap: Record<string, number> = {};
        ordersRes.data.forEach((o) => {
          statusMap[o.status] = (statusMap[o.status] || 0) + 1;
        });
        setOrderStats(statusMap);
        setRecentOrders(ordersRes.data.slice(0, 5));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <div className="dash-loading">Загрузка...</div>;

  const totalDebt = shops.reduce((s, sh) => s + Number(sh.debt), 0);
  const totalOrders = Object.values(orderStats).reduce((a, b) => a + b, 0);
  const pending = orderStats['PENDING'] || 0;

  return (
    <div className="dashboard">
      <h1 className="dash-title">Добро пожаловать, {user?.firstName}!</h1>

      <div className="kpi-grid">
        <div className="kpi-card kpi-purple">
          <div className="kpi-icon">🏪</div>
          <div className="kpi-body">
            <span className="kpi-label">Мои магазины</span>
            <span className="kpi-value">{shops.length}</span>
          </div>
        </div>
        <div className="kpi-card kpi-blue">
          <div className="kpi-icon">📋</div>
          <div className="kpi-body">
            <span className="kpi-label">Всего заказов</span>
            <span className="kpi-value">{totalOrders}</span>
          </div>
        </div>
        <div className="kpi-card kpi-amber">
          <div className="kpi-icon">⏳</div>
          <div className="kpi-body">
            <span className="kpi-label">Ожидают</span>
            <span className="kpi-value">{pending}</span>
          </div>
        </div>
        <div className={`kpi-card ${totalDebt > 0 ? 'kpi-red' : 'kpi-green'}`}>
          <div className="kpi-icon">💳</div>
          <div className="kpi-body">
            <span className="kpi-label">Мой долг</span>
            <span className="kpi-value">{fmt(totalDebt)} с</span>
          </div>
        </div>
      </div>

      <div className="dash-row">
        {/* My shops balance */}
        <div className="dash-card">
          <h2 className="dash-card-title">Мои магазины</h2>
          {shops.length === 0 ? (
            <p className="dash-empty">Нет магазинов</p>
          ) : (
            <div className="debt-list">
              {shops.map((shop) => {
                const pct = Number(shop.creditLimit) > 0
                  ? Math.min(100, (Number(shop.debt) / Number(shop.creditLimit)) * 100)
                  : 0;
                const over = Number(shop.creditLimit) > 0 && Number(shop.debt) >= Number(shop.creditLimit);
                return (
                  <div key={shop.id} className="debt-row">
                    <div className="debt-row-top">
                      <span className="debt-name">{shop.name}</span>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                        {Number(shop.debt) > 0 && (
                          <span className="debt-amount" style={{ color: over ? '#ef4444' : '#1e40af' }}>
                            Долг: {fmt(shop.debt)} с
                          </span>
                        )}
                        {Number(shop.advance) > 0 && (
                          <span className="debt-amount" style={{ color: '#065f46' }}>
                            Аванс: +{fmt(shop.advance)} с
                          </span>
                        )}
                        {Number(shop.debt) === 0 && Number(shop.advance) === 0 && (
                          <span className="debt-amount" style={{ color: '#9ca3af' }}>0 с</span>
                        )}
                      </div>
                    </div>
                    {Number(shop.creditLimit) > 0 && (
                      <>
                        <div className="debt-bar-wrap">
                          <div className="debt-bar" style={{ width: `${pct}%`, background: over ? '#ef4444' : '#3b82f6' }} />
                        </div>
                        <span className="debt-limit">
                          {over ? '⚠️ Лимит превышен! ' : ''}лимит: {fmt(shop.creditLimit)} с
                        </span>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="dash-card dash-card-wide">
          <RecentOrdersWidget orders={recentOrders} showAgent={true} viewAllLink="/orders" />
        </div>
      </div>
    </div>
  );
};

// ─── Root ─────────────────────────────────────────────────────────────────────
const Dashboard: React.FC = () => {
  const { isAdmin, isAgent, isShopOwner } = useAuth();
  if (isAdmin()) return <AdminDashboard />;
  if (isAgent()) return <AgentDashboard />;
  if (isShopOwner()) return <ShopOwnerDashboard />;
  return <div className="dash-loading">Загрузка...</div>;
};

export default Dashboard;
